/**
 * QuestionnaireFillPage — Batch #14.242
 *
 * Page PUBLIQUE (magic link, aucune connexion) : /questionnaire/:token
 * Le client répond aux questions (texte, Oui/Non, choix, date, case), lit les sections
 * texte (ex. confidentialité), puis signe — la date est posée automatiquement.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileSignature, CheckCircle2, Loader2, Download } from "lucide-react";
import { toast } from "@/lib/toast";
import { SignatureCanvas } from "@/pages/public/FiscalMandatePage";
import { API } from "@/api";

const NAVY = "#002a80";
const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900/30";

function PillChoice({ options, value, onChange, testPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(o)}
          className={`text-sm px-4 py-1.5 rounded-lg border transition ${value === o ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
          style={value === o ? { background: NAVY } : {}} data-testid={`${testPrefix}-${o}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

function QuestionInput({ q, value, onChange }) {
  const tid = `qn-answer-${q.id}`;
  if (q.type === "checkbox") {
    return (
      <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 mt-0.5 accent-blue-900" data-testid={tid} />
        <span>{q.label}{q.required && <span className="text-red-600"> *</span>}</span>
      </label>
    );
  }
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">
        {q.label}{q.required && <span className="text-red-600"> *</span>}
      </label>
      {q.type === "long" && <textarea rows={3} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />}
      {q.type === "short" && <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />}
      {q.type === "date" && <input type="date" className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />}
      {q.type === "yesno" && <PillChoice options={["Oui", "Non"]} value={value} onChange={onChange} testPrefix={tid} />}
      {q.type === "choice" && <PillChoice options={q.options || []} value={value} onChange={onChange} testPrefix={tid} />}
    </div>
  );
}

function TextBody({ body }) {
  const fmt = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") ? <b key={i}>{part.slice(2, -2)}</b> : part);
  return (
    <div className="text-sm text-slate-700 leading-relaxed space-y-1.5">
      {body.split("\n").map((line, i) => {
        const t = line.trim();
        if (!t) return <div key={i} className="h-1" />;
        if (/^[•\-*] /.test(t)) return <p key={i} className="pl-4 relative"><span className="absolute left-0">•</span>{fmt(t.slice(2))}</p>;
        return <p key={i}>{fmt(t)}</p>;
      })}
    </div>
  );
}

function Section({ s, answers, setAnswer }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      {s.heading && <div className="px-4 py-2.5 text-white text-sm font-bold" style={{ background: NAVY }}>{s.heading}</div>}
      <div className="p-4 space-y-3">
        {s.type === "text"
          ? <TextBody body={s.body || ""} />
          : (s.questions || []).map((q) => <QuestionInput key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />)}
      </div>
    </div>
  );
}

export default function QuestionnaireFillPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [info, setInfo] = useState(null);
  const [answers, setAnswers] = useState({});
  const [signature, setSignature] = useState("");
  const [hasSignature, setHasSignature] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/questionnaire/${token}`);
        setInfo(r.data);
        if (r.data.status === "completed") setDone(true);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const pdfUrl = `${API}/public/questionnaire/${token}/pdf`;
  const setAnswer = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  const submit = async () => {
    const missing = (info.sections || []).flatMap((s) => s.questions || [])
      .find((q) => q.required && (q.type === "checkbox" ? !answers[q.id] : !String(answers[q.id] || "").trim()));
    if (missing) { toast.error(`Veuillez répondre : « ${missing.label} »`); return; }
    if (!hasSignature) { toast.error("Veuillez signer dans le cadre"); return; }
    if (!consent) { toast.error("Veuillez confirmer l'exactitude de vos réponses"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/questionnaire/${token}/submit`, { answers, signature_image_b64: signature, consent: true });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi — réessayez");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }
  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center max-w-md" data-testid="qn-not-found">
          <FileSignature className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <h1 className="text-lg font-bold text-slate-800 mb-1">Lien invalide</h1>
          <p className="text-sm text-slate-500">Ce lien n'existe pas ou n'est plus disponible. Contactez DG Comptabilité au 418 376-4298.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="questionnaire-fill-page">
      <div className="text-white py-6 px-4" style={{ background: NAVY }}>
        <div className="max-w-3xl mx-auto">
          <h1 className="text-xl sm:text-2xl font-bold">DG Comptabilité</h1>
          <p className="text-sm opacity-80 mt-0.5">{info.title} — {info.client_name}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-6 space-y-4">
        {done ? (
          <div className="bg-white rounded-2xl border-2 border-emerald-200 p-8 text-center" data-testid="qn-done">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500 mb-3" />
            <h2 className="text-xl font-bold text-slate-800 mb-1">Merci !</h2>
            <p className="text-sm text-slate-600 mb-4">Vos réponses ont bien été reçues et votre questionnaire est signé avec la date du jour.</p>
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-bold"
              style={{ background: NAVY }} data-testid="qn-download-signed">
              <Download className="w-4 h-4" /> Télécharger mon questionnaire signé
            </a>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-600">
              Bonjour {info.client_name} 👋 — veuillez répondre aux questions ci-dessous, lire les sections d'information, puis signer.
              Les champs marqués <span className="text-red-600">*</span> sont obligatoires.
            </p>
            {info.notes && <p className="text-sm text-slate-500 italic bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">« {info.notes} »</p>}

            {(info.sections || []).map((s, i) => <Section key={i} s={s} answers={answers} setAnswer={setAnswer} />)}

            <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3">
              <h3 className="text-sm font-bold text-slate-800">Confirmation et signature</h3>
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="w-4 h-4 mt-0.5 accent-blue-900" data-testid="qn-consent" />
                <span>J'atteste que les renseignements fournis sont exacts et complets, et j'ai pris connaissance des sections d'information ci-dessus.</span>
              </label>
              <SignatureCanvas onChange={(dataUrl, has) => { setSignature(dataUrl); setHasSignature(has); }} />
              <p className="text-[11px] text-slate-400">La date du jour sera inscrite automatiquement au moment de la signature.</p>
              <button onClick={submit} disabled={submitting || !hasSignature || !consent}
                className="w-full sm:w-auto px-6 py-3 rounded-xl text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40"
                style={{ background: NAVY }} data-testid="qn-submit-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                {submitting ? "Envoi en cours..." : "Soumettre mes réponses signées"}
              </button>
            </div>
          </>
        )}
        <p className="text-[11px] text-slate-400 text-center pt-4">
          418 376-4298 · d.gougeon@outlook.fr · 412, rue J-R. Théberge, Chicoutimi, QC, G7J 3R9
        </p>
      </div>
    </div>
  );
}
